import React from 'react';
import { Container, Box, Grid, Skeleton } from '@mui/material';
import { theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';

const PAGE_CONTENT_PX = { xs: 3, sm: 3.5, md: 4, lg: 5, xl: 5 };
const PAGE_CONTENT_MAX = 1120;
const CARD_BG = '#1a1a1a';
const CARD_BORDER = '1px solid rgba(255,255,255,0.06)';

const CompanyContainerSkeleton: React.FC = () => {
    const { themeStyle } = useThemeContext();

    return (
        <Container
            disableGutters
            style={{
                height: 'auto',
                justifyContent: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
            }}
            sx={{
                width: '100%',
                marginLeft: 0,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
                overflowX: 'hidden',
                minHeight: '70vh',
                backgroundColor: theme[themeStyle].pure_black,
            }}
            maxWidth={false}>
            {/* Full-bleed hero skeleton */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 280, sm: 360, md: 420 },
                    bgcolor: theme[themeStyle].deep_grey,
                    borderRadius: 0,
                    overflow: 'hidden',
                }}>
                <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%', bgcolor: 'transparent' }} />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        p: { xs: 2.5, sm: 3, md: 4 },
                        '@media (max-width: 360px)': {
                            px: 2,
                            py: 2,
                        },
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: { xs: 560, md: '100%' },
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: { xs: 2, sm: 3 },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 1.25, sm: 2 }, minWidth: 0 }}>
                        <Skeleton
                            variant="rounded"
                            sx={{
                                width: { xs: 56, sm: 80 },
                                height: { xs: 56, sm: 80 },
                                borderRadius: 2,
                                        bgcolor: theme[themeStyle].deep_grey,
                                flexShrink: 0,
                            }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Skeleton
                                variant="text"
                                width="68%"
                                height={32}
                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 0.5 }}
                            />
                            <Skeleton
                                variant="text"
                                width="40%"
                                height={20}
                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Skeleton
                                    variant="rounded"
                                    width={64}
                                    height={24}
                                    sx={{ borderRadius: 999, bgcolor: theme[themeStyle].deep_grey }}
                                />
                                <Skeleton
                                    variant="text"
                                    width={82}
                                    height={16}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Skeleton
                            variant="rounded"
                            width="100%"
                            height={44}
                            sx={{ borderRadius: 999, bgcolor: theme[themeStyle].deep_grey }}
                        />
                        <Skeleton
                            variant="rounded"
                            width="100%"
                            height={44}
                            sx={{ borderRadius: 999, bgcolor: theme[themeStyle].deep_grey }}
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    maxWidth: PAGE_CONTENT_MAX,
                    mx: 'auto',
                    px: PAGE_CONTENT_PX,
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 3 },
                    boxSizing: 'border-box',
                    minWidth: 0,
                }}>
                <Grid
                    container
                    spacing={{ xs: 3, lg: 4 }}
                    sx={{
                        mt: 0,
                        '@media (width: 344px) and (height: 882px)': {
                            '--Grid-columnSpacing': '12px',
                            '--Grid-rowSpacing': '24px',
                        },
                    }}>
                    <Grid item xs={12} lg={8}>
                        <Box
                            sx={{
                                backgroundColor: CARD_BG,
                                border: CARD_BORDER,
                                borderRadius: 2,
                                mb: 3,
                                p: 3,
                            }}>
                            <Skeleton
                                variant="text"
                                width="45%"
                                height={20}
                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                            />
                            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                                <Skeleton
                                    variant="text"
                                    width={120}
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                                <Skeleton
                                    variant="text"
                                    width={100}
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                                <Skeleton
                                    variant="text"
                                    width={140}
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Skeleton
                                    variant="text"
                                    width="100%"
                                    height={16}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="90%"
                                    height={16}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="80%"
                                    height={16}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: CARD_BG,
                                border: CARD_BORDER,
                                borderRadius: 2,
                            }}>
                            <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', p: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {Array.from({ length: 6 }).map((_, index) => (
                                        <Skeleton
                                            key={index}
                                            variant="text"
                                            width={80}
                                            height={24}
                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={400}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: 1 }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Box sx={{ spaceY: 3 }}>
                            <Box
                                sx={{
                                    backgroundColor: CARD_BG,
                                    border: CARD_BORDER,
                                    borderRadius: 2,
                                    mb: 3,
                                    p: 3,
                                }}>
                                <Skeleton
                                    variant="text"
                                    width="50%"
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Skeleton
                                                variant="text"
                                                width={80}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={60}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    backgroundColor: CARD_BG,
                                    border: CARD_BORDER,
                                    borderRadius: 2,
                                    mb: 3,
                                    p: 3,
                                }}>
                                <Skeleton
                                    variant="text"
                                    width="40%"
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Skeleton
                                                variant="circular"
                                                width={40}
                                                height={40}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton
                                                    variant="text"
                                                    width="70%"
                                                    height={20}
                                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    width="50%"
                                                    height={16}
                                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    backgroundColor: CARD_BG,
                                    border: CARD_BORDER,
                                    borderRadius: 2,
                                    p: 3,
                                }}>
                                <Skeleton
                                    variant="text"
                                    width="60%"
                                    height={20}
                                    sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Skeleton
                                                variant="rounded"
                                                width={32}
                                                height={32}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton
                                                    variant="text"
                                                    width="60%"
                                                    height={20}
                                                    sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                />
                                            </Box>
                                            <Skeleton
                                                variant="text"
                                                width={40}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export { CompanyContainerSkeleton };
